/**
 * Passcode gate. Unlocks the encrypted data blob and seeds the store.
 */
import { store } from '../store.ts';
import { decryptJSON } from '../crypto.ts';
import { saveSummary, loadSummary } from '../storage.ts';
import type { WardrobeSummary } from '../types.ts';

const TPL = /* html */ `
<style>
:host {
  display: grid;
  place-items: center;
  inset: 0;
  background:
    radial-gradient(circle at 22% 18%, color-mix(in oklch, var(--brass) 10%, transparent) 0%, transparent 44%),
    radial-gradient(circle at 80% 78%, color-mix(in oklch, var(--brass) 7%, transparent) 0%, transparent 46%),
    linear-gradient(180deg, var(--paper) 0%, var(--paper-deep) 100%);
  overflow: hidden;
}
.wrap {
  text-align: center;
  display: grid;
  gap: 18px;
  padding: 32px 28px;
  max-width: 440px;
  position: relative;
  isolation: isolate;
}
.issue {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--rule);
  font-family: var(--mono);
  font-size: 9px;
  letter-spacing: 0.42em;
  text-transform: uppercase;
  color: var(--brass-dim);
  gap: 12px;
}
.issue strong { color: var(--ink); letter-spacing: 0.44em; font-weight: 700; }
.hanger {
  width: 96px;
  height: 76px;
  margin: 4px auto -8px;
  color: var(--brass);
  opacity: 0.7;
  animation: hanger-sway 5200ms cubic-bezier(0.45, 0, 0.55, 1) infinite;
  transform-origin: 50% 12%;
}
.mark {
  font-family: var(--hindi);
  font-size: clamp(82px, 22vw, 156px);
  font-weight: 400;
  color: var(--brass);
  line-height: 0.88;
  letter-spacing: 0.006em;
  margin-top: 4px;
  text-shadow: 0 1px 0 color-mix(in oklch, var(--brass-deep) 22%, transparent);
}
.en {
  font-family: var(--mono);
  font-size: 12px;
  letter-spacing: 0.56em;
  text-transform: uppercase;
  color: var(--ink);
  font-weight: 700;
  margin-top: 4px;
  text-indent: 0.56em;
}
.brass-rule {
  height: 1px;
  background: linear-gradient(90deg, transparent 0%, var(--brass) 50%, transparent 100%);
  margin-block: 10px;
  opacity: 0.6;
}
.quote {
  font-family: var(--display);
  font-size: 19px;
  font-style: italic;
  color: var(--ink-dim);
  font-weight: 500;
  line-height: 1.4;
  text-wrap: pretty;
}
.quote::before { content: '\\201C'; color: var(--brass); margin-right: 2px; font-style: normal; }
.quote::after { content: '\\201D'; color: var(--brass); margin-left: 2px; font-style: normal; }
.attrib {
  font-family: var(--mono);
  font-size: 9px;
  letter-spacing: 0.36em;
  text-transform: uppercase;
  color: var(--ink-faint);
  margin-top: 2px;
}
.field {
  display: grid;
  gap: 10px;
  margin-top: 6px;
}
input {
  font-family: var(--mono);
  font-size: 16px;
  text-align: center;
  padding: 16px 14px;
  background: var(--paper-up);
  border: 1px solid var(--rule);
  border-radius: 4px;
  color: var(--ink);
  outline: none;
  letter-spacing: 0.44em;
  transition: border-color 200ms var(--tx-snap), background 200ms;
}
input:focus {
  border-color: var(--brass);
  background: var(--paper-card);
}
input::placeholder { color: var(--ink-faint); letter-spacing: 0.22em; }
button {
  font-family: var(--mono);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.42em;
  padding: 16px;
  background: var(--ink-deep);
  color: var(--paper);
  border-radius: 4px;
  font-weight: 700;
  transition: transform 140ms var(--tx-snap), filter 140ms, letter-spacing 220ms ease;
}
button:hover { letter-spacing: 0.5em; filter: brightness(1.08); }
button:active { transform: scale(0.97); filter: brightness(1.12); }
.hint {
  font-family: var(--mono);
  font-size: 9px;
  color: var(--ink-faint);
  margin-top: 6px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
}
.err {
  font-family: var(--mono);
  font-size: 11px;
  color: var(--rose);
  letter-spacing: 0.06em;
  min-height: 14px;
}
@keyframes hanger-sway {
  0%, 100% { transform: rotate(-0.8deg); }
  50%      { transform: rotate(0.8deg); }
}
@media (prefers-reduced-motion: reduce) {
  .hanger { animation: none; }
}
</style>
<div class="wrap">
  <div class="issue">
    <span><strong>Issue I</strong> &middot; MMXXVI</span>
    <span>Wardrobe Atelier</span>
  </div>
  <svg class="hanger" viewBox="0 0 80 60" aria-hidden="true">
    <path d="M40 8 L40 18 M28 14 a12 12 0 1 1 24 0 M10 50 L40 22 L70 50 Z M10 50 L70 50" fill="none" stroke="currentColor" stroke-width="0.9" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>
  <div>
    <div class="mark">अलमारी</div>
    <div class="en">A &middot; L &middot; M &middot; A &middot; R &middot; I</div>
  </div>
  <div class="brass-rule"></div>
  <div class="quote">khaali almari sapna hai, bhari almari kahaani.</div>
  <div class="attrib">— closet atelier &middot; vol. i</div>
  <form class="field" id="form">
    <input id="pass" type="password" placeholder="passcode" autocomplete="off" autocapitalize="off" autocorrect="off" inputmode="text" />
    <button type="submit" id="btn">Unlock the archive</button>
    <div class="err" id="err"></div>
  </form>
  <div class="hint">on-device &middot; encrypted &middot; nothing leaves your phone</div>
</div>
`;

export class AlmariGate extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });

  connectedCallback(): void {
    this.#shadow.innerHTML = TPL;
    const form = this.#shadow.getElementById('form') as HTMLFormElement;
    const pass = this.#shadow.getElementById('pass') as HTMLInputElement;
    const err = this.#shadow.getElementById('err') as HTMLDivElement;

    queueMicrotask(() => pass.focus());

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      err.textContent = '';
      const passcode = pass.value.trim();
      if (!passcode) return;
      try {
        const summary = await this.#unlock(passcode);
        await saveSummary(summary);
        sessionStorage.setItem('almari:passcode', passcode);
        this.#transitionToApp(summary);
      } catch (e2) {
        err.textContent = 'galat passcode';
        pass.value = '';
        pass.focus();
      }
    });
  }

  async #unlock(passcode: string): Promise<WardrobeSummary> {
    const cached = await loadSummary();
    if (cached) return cached;
    const res = await fetch('/data.encrypted.json', { cache: 'no-cache' });
    if (!res.ok) throw new Error(`fetch failed: ${res.status}`);
    const body = await res.json() as { ciphertext: string };
    return decryptJSON<WardrobeSummary>(passcode, body.ciphertext);
  }

  #transitionToApp(summary: WardrobeSummary): void {
    const apply = (): void => {
      store.patch({ phase: 'ready', summary, error: null });
    };
    if ('startViewTransition' in document) {
      (document as Document & { startViewTransition: (cb: () => void) => unknown }).startViewTransition(apply);
    } else {
      apply();
    }
  }
}

customElements.define('almari-gate', AlmariGate);
